import { FormikProps, FormikHelpers } from 'formik';

import { AmountValue, CalculationMode, SingleValueResponse } from 'common/interfaces';
import { API_ACCOUNT_FINANCE_INSURANCE, API_ACCOUNT_DETAIL_FINANCE_INSURANCE } from 'api/endpoints';

import { FormStore } from '../../formStore';
import { RootStore } from '../../rootStore';
import { RentModalsStore } from 'stores/workflowStores/finances/rentModalsStore';

type InsuranceStrategy = 'UNDER_25' | 'FROM_25_TO_49' | 'FROM_50_TO_74' | 'OVER_74';

interface InsuranceData {
  balance: AmountValue;
  periodicPerson: AmountValue;
  periodicEmployer: AmountValue;
  strategy: InsuranceStrategy;
  calculationMode: CalculationMode;
  plannedRent: AmountValue;
  calculatedRent: AmountValue;
}

interface Options {
  strategy: InsuranceStrategy[];
  calculationMode: CalculationMode[];
}

export interface Insurance {
  clientData: InsuranceData;
  partnerData: InsuranceData;
  defaultData: InsuranceData;
  options: Options;
}

export class WorkflowInsuranceStore extends FormStore<Insurance> {
  accountId: string;
  detailMode?: boolean;
  rentModalsStore: RentModalsStore;

  constructor(rootStore: RootStore, rentModalsStore: RentModalsStore, accountId: string, detailMode?: boolean) {
    super(rootStore);
    this.accountId = accountId;
    this.rentModalsStore = rentModalsStore;
    this.detailMode = detailMode;
  }

  silentUpdate = async (formikProps: FormikProps<Insurance>) => {
    const { post } = this.rootStore.stores.apiStore;
    const { values } = formikProps;

    // those zeroes had to be set for calculator to work correctly, because it doesn't calculate
    // when there is not zero as calculated rent - it simply return what's given.
    const response = await post<SingleValueResponse<number> | { errors: { key: string[] } }>(
      this.detailMode
        ? API_ACCOUNT_DETAIL_FINANCE_INSURANCE(this.accountId)
        : API_ACCOUNT_FINANCE_INSURANCE(this.accountId),
      {
        clientData: values.clientData
          ? {
              ...values.clientData,
              calculatedRent: {
                value: 0,
                currency: values.clientData.calculatedRent.currency,
              },
            }
          : undefined,
        partnerData: values.partnerData
          ? {
              ...values.partnerData,
              calculatedRent: {
                value: 0,
                currency: values.partnerData.calculatedRent.currency,
              },
            }
          : undefined,
        defaultData: values.defaultData,
      },
    );

    if (response?.data) {
      const newValues = await this.silentPost(formikProps);

      if (newValues) {
        const { data, errors } = newValues;
        const { clientData, partnerData } = data!;

        if (clientData && clientData.calculationMode === CalculationMode.CALCULATED) {
          formikProps.setFieldValue('clientData.calculatedRent', clientData.calculatedRent);
        }

        if (partnerData && partnerData.calculationMode === CalculationMode.CALCULATED) {
          formikProps.setFieldValue('partnerData.calculatedRent', partnerData.calculatedRent);
        }

        if (errors) {
          this.setErrors(response?.errors || [], formikProps, 4);
        }
      }
    }

    if (response?.errors) {
      this.setErrors(response.errors, formikProps, 4);
    }
  };

  silentPost = async (formikProps: FormikProps<Insurance>) => {
    const { get } = this.rootStore.stores.apiStore;
    return await get<Insurance>(
      this.detailMode
        ? API_ACCOUNT_DETAIL_FINANCE_INSURANCE(this.accountId)
        : API_ACCOUNT_FINANCE_INSURANCE(this.accountId),
    );
  };

  fetchInsurance = async () => {
    this.rentModalsStore.setLoading(true);
    this.fetchData(
      this.detailMode
        ? API_ACCOUNT_DETAIL_FINANCE_INSURANCE(this.accountId)
        : API_ACCOUNT_FINANCE_INSURANCE(this.accountId),
    );
    this.rentModalsStore.setLoading(false);
  };

  submitInsurance = async (values: Insurance, formikHelpers: FormikHelpers<Insurance>) => {
    return await this.submitData(
      this.detailMode
        ? API_ACCOUNT_DETAIL_FINANCE_INSURANCE(this.accountId)
        : API_ACCOUNT_FINANCE_INSURANCE(this.accountId),
      values,
      {
        validationPrefixDepth: 4,
        formikHelpers,
        singleValueResponse: true,
      },
    );
  };
}
